import React from "react";
import { Link } from "react-router-dom";
import { DOCS_URL } from "../lib/links";

/**
 * The landing screen.
 *
 * It states what the application computes before it asks for anything, which the
 * run list could not: a scientist arriving at the URL first met an empty table.
 *
 * Kept to statements of method rather than description. Anything that reads as
 * promotion belongs nowhere in this product, and the detail belongs in the
 * documentation, which is one control away.
 *
 * Deliberately API-free. Nothing here is fetched, so the page still explains the
 * application and still links to the documentation when the backend is down —
 * which is also when someone is most likely to be reading it.
 */

const STEPS = [
  {
    n: 1,
    title: "Upload",
    body:
      "Three LC-MS/MS exports and the Substanzen workbook. Each export's condition is read from inside the file and cross-checked against its name; the anonymous Nr.x series are matched back to compounds and shown for confirmation.",
  },
  {
    n: 2,
    title: "Review QC",
    body:
      "Internal-standard trace per condition, calibration, uninhibited control mean and CV, and the flags raised for carry-over, completeness and the positive-control windows. Nothing is discarded automatically.",
  },
  {
    n: 3,
    title: "Curate and export",
    body:
      "The observations table beside the fitted curve, all three conditions overlaid. Excluding a point re-fits immediately and reports the change; the workbook and the figures follow the curation.",
  },
];

/* Verified against app/evaluwhale/: parsers/lcms.py, dilution.py, normalise.py,
   fitting.py. Change a row here only when that code changes. */
const METHOD = [
  ["Assay", "CYP3A4 inhibition, midazolam as substrate"],
  [
    "Conditions",
    "Co-incubation, pre-incubation −NADPH, pre-incubation +NADPH — one instrument export each",
  ],
  [
    "Response",
    "Calculated concentration (ng/mL) per injection, quantified by the instrument against its own calibration with 13C6-1-OH-midazolam as internal standard",
  ],
  [
    "Concentrations",
    "Six-point twofold dilution series from each compound's Konzentration im Ansatz value",
  ],
  [
    "Normalisation",
    "Percent of the mean of the six Nullwert injections of the same condition",
  ],
  [
    "Model",
    "Constrained Hill (four-parameter logistic), Top = 100 and Bottom = 0 fixed; log10(IC50) and Hill slope fitted by unweighted least squares over individual observations, replicate and concentration blocks pooled",
  ],
  [
    "Reported",
    "IC50 with a 95 % confidence interval, Hill slope, R², residual standard error, and a range classification",
  ],
  [
    "Curation",
    "Points are excluded with a reason, a name and a timestamp, and can be restored. No measurement is deleted",
  ],
  [
    "Export",
    "Excel workbook with the figures and the audit trail embedded, or the curves as 300 dpi images",
  ],
];

export default function HomeScreen() {
  return (
    <div className="home">
      <section className="hero">
        <h1>Evaluwhale</h1>
        <p className="hero-tagline">
          CYP3A4 inhibition assay analysis: quality control, concentration–response
          fitting and export.
        </p>
        <p className="hero-body">
          Evaluwhale analyses CYP3A4 (midazolam) inhibition runs from the LC-MS/MS exports
          and the Substanzen workbook: it reconstructs which series is which compound,
          normalises each response to percent of uninhibited control, and fits a curve per
          compound and condition. It replaces the macro-driven <code>processing.xlsm</code>
          , in which the IC50 was interpolated between the two points either side of 50 %
          and excluded points were deleted in place.
        </p>
        <div className="hero-actions">
          <Link className="button-link primary" to="/runs">
            Dashboard
          </Link>
          <a className="button-link" href={DOCS_URL} target="_blank" rel="noopener noreferrer">
            Documentation
          </a>
        </div>
      </section>

      <section>
        <h2 className="home-heading">A run, in three steps</h2>
        <div className="home-steps">
          {STEPS.map((step) => (
            <div className="card home-step" key={step.n}>
              <div className="home-step-number">{step.n}</div>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="home-heading">Method</h2>
        <div className="card">
          <table className="method">
            <tbody>
              {METHOD.map(([term, value]) => (
                <tr key={term}>
                  <th scope="row">{term}</th>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Nothing here has been scientifically signed off, and the application has
          no authentication. Both belong on the first screen rather than in a
          document somebody has to go and find. */}
      <section className="banner warning home-status">
        <strong>Implemented, not approved.</strong> The positive-control IC50 values
        reproduce the historically published ones on the single run checked so far, but no
        scientist has signed off the model, the QC thresholds or the figures, and there is
        no authentication in front of the application. What is settled, what is assumed and
        what is open is set out in the{" "}
        <a href={DOCS_URL} target="_blank" rel="noopener noreferrer">
          documentation
        </a>
        .
      </section>
    </div>
  );
}
